import { lazy } from "react";

const pages = new Map();          // name → load()
const RELOADED_KEY = "chunk_reload_once";

// After a new deploy, old page files no longer exist on the server ("ChunkLoadError").
// Reload the site once to get the new files instead of showing a blank page.
function withDeployRetry(factory) {
  return () =>
    factory()
      .then((m) => {
        try { sessionStorage.removeItem(RELOADED_KEY); } catch { /* storage blocked */ }
        return m;
      })
      .catch((err) => {
        const isChunkError = /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i
          .test(String(err && (err.name + " " + err.message)));
        let reloaded = false;
        try { reloaded = sessionStorage.getItem(RELOADED_KEY) === "1"; } catch { /* storage blocked */ }
        if (isChunkError && !reloaded) {
          try { sessionStorage.setItem(RELOADED_KEY, "1"); } catch { /* storage blocked */ }
          window.location.reload();
          return new Promise(() => {});   // keep waiting while the page reloads
        }
        throw err;
      });
}

/**
 * Like React.lazy, plus .preload(). `factory` must resolve to a module with a default export
 * (for named exports: () => import("./X").then((m) => ({ default: m.X }))).
 */
export function lazyWithPreload(factory, name) {
  let promise = null;
  const load = () => {
    if (!promise) promise = withDeployRetry(factory)().catch((err) => { promise = null; throw err; });
    return promise;
  };
  const Component = lazy(load);
  Component.preload = load;
  pages.set(name || `page${pages.size}`, load);
  return Component;
}

/** Download one page's code now (e.g. on menu hover). Safe to call many times. */
export function preloadPage(name) {
  const load = pages.get(name);
  if (load) load().catch(() => { /* real error shows when the page is opened */ });
}

const idle = (fn) =>
  typeof window !== "undefined" && window.requestIdleCallback
    ? window.requestIdleCallback(fn, { timeout: 3000 })
    : setTimeout(fn, 300);

let started = false;
/**
 * After login: download every page's code in the background, one at a time, only when the browser is idle
 * (doesn't slow down the page the user is on). Call once; later calls do nothing.
 */
export function preloadAllPages({ delayMs = 1500 } = {}) {
  if (started) return;
  started = true;
  // Slow / data-saver connections: don't download everything, pages still load on click
  const conn = typeof navigator !== "undefined" && navigator.connection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ""))) return;

  const queue = [...pages.values()];
  const next = () => {
    const load = queue.shift();
    if (!load) return;
    load().catch(() => {}).finally(() => idle(next));
  };
  setTimeout(() => idle(next), delayMs);
}